import { Box, Typography, Grid, Card } from "@mui/material";
import { useMemo, useState } from "react";
import { MaxDropLimits, ReportValueCardProps } from "helpers/types";
import { StrategyReportThemedCardValuesLabels } from "helpers/constants";
import { drawdownComparisonChartStyles } from "../../../../styles/DrawdownComparisonChart.styles";
import { DrawdownComparisonEditModal } from "./DrawdownComparisonEditModal";
import {
  getBarDirection,
  getBarOffset,
  getBarWidth,
  getBarWidthPercentage,
} from "./comparisonChartUtils";
import { asPercentage, toFiniteNumber } from "components/ui/formatters";
import {
  NO_REPORT_DISPLAY,
  UNAVAILABLE_DISPLAY,
} from "../reportFieldStates";

/**
 * What ONE bar of the comparison shows: the figure it draws, or the state
 * that says there is none.
 *
 * `toFiniteNumber(row?.value) ?? 0` stood where this does, and it was the
 * epic-#482 failure moved out of a field and into a chart. Every input these
 * rows hold that is NOT a figure -- the "No Report" seed, the empty sentinel
 * behind a refused figure, a null the payload never filled in -- read as 0
 * and drew a 0.0% bar. On a DRAWDOWN chart that is not a blank: it asserts a
 * portfolio that did not fall at all, which is a made-up result, and it did
 * so while the field directly above it on the same card was saying "No
 * Report" about the very same row.
 *
 * A chart cannot draw a state, so it SAYS it instead -- the same answer the
 * sibling allocation donut already gives (CurrentAllocationChartReport), and
 * the reason both refuse rather than substitute a zero.
 *
 * The order of the two states is the cards' own (reportFieldStates): a
 * REASON outranks a seed, because a reason means the backend reached this
 * figure and refused it, which is an answer -- where the seed reports only
 * that nothing has run yet.
 */
const drawdownRowFigure = (
  row: ReportValueCardProps | undefined,
): { figure: number | null; state: string | null } => {
  // Read out once: a MISSING row and a row whose value is nullish are the
  // same thing to everything below, and narrowing the value rather than the
  // row is what lets the seed be echoed verbatim without re-reading it.
  const value = row?.value;
  // The REASON is tested before the figure, and that order is the card's
  // rather than the convenient one: MultiValuesCard renders N/A whenever a
  // reason is present, figure or no figure, because a reason means the
  // backend reached this number and would not stand behind it. Testing the
  // figure first would draw a bar beside a field reading N/A -- the very
  // screen-vs-chart disagreement this function exists to end. Not reachable
  // from the Protect rows today, which carry no reason on either drawdown
  // row; written to the card's rule so it stays true if one starts to.
  if (row?.unavailableReason?.trim()) {
    return { figure: null, state: UNAVAILABLE_DISPLAY };
  }
  const figure = toFiniteNumber(value);
  if (figure !== null) return { figure, state: null };
  // Everything else non-numeric is ONE state, and deliberately so: the
  // seed these two rows carry, "No Report", which is also the only honest
  // thing to say about the rest.
  //
  // This is where the chart stops echoing the row's value verbatim, and the
  // reason is the column it has to fit. `isPlaceholderDisplay` admits
  // NOT_IMPLEMENTED and "Select stock" as well, and either would overflow a
  // 70px value column -- fifteen characters against a nine-character budget
  // -- so echoing whatever arrived would ship the unreadable-state failure
  // that column's own sizing exists to prevent. Neither can reach a drawdown
  // row anyway: both are seeded NO_REPORT and an operator can only type a
  // number over them. What is left is a corrupt stored value, and of that
  // "No Report" is the weaker of the two claims available and the only true
  // one -- N/A would assert a refusal the backend never made.
  return { figure: null, state: NO_REPORT_DISPLAY };
};

/**
 * The BARS are read off the Protect card's own rows (`values`), so a row the
 * backend refused or never filled draws no bar and says so -- the epic-#482
 * rule this chart is held to.
 *
 * The LIMITS are the y-axis bounds and arrive resolved: useMaxDropLimits
 * derives them from this protect asset's own two drops and stores the
 * operator's override per asset, so they are already numbers by here and the
 * union the old `limit?: number | string` carried is gone with the `as number`
 * at the call site.
 */
interface DrawdownComparisonChartProps {
  values: ReportValueCardProps[];
  maxDrop: number;
  negativeLimit: number;
  onLimitsChange: (limits: MaxDropLimits) => void;
}

// These bars read to one decimal. Shared rather than kept privately: the
// duplicate this replaces was an unguarded `value.toFixed`, the same shape as
// the one that crashed the page from the sibling chart.
const asBarLabel = (value: number) => asPercentage(value, 1);

export const DrawdownComparisonChart = ({
  values,
  maxDrop,
  negativeLimit,
  onLimitsChange,
}: DrawdownComparisonChartProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Both read through toFiniteNumber rather than coercing the row themselves,
  // which is the whole reason that function is shared: each of these was a
  // private copy of the same reading, and each carried the same throw --
  // `?.` covered a MISSING row but not a row whose value is null, so
  // `.toString()` on it was an uncaught TypeError. The two are also why the
  // guard could not just live in the cards: this chart reads the Protect
  // card's rows directly, so a null reaches it without passing through either
  // card. Every input these rows actually hold -- a number, "", a seed --
  // resolves to the same figure it did before.
  const stock = useMemo(
    () =>
      drawdownRowFigure(
        values.find(
          (v) =>
            v.label === StrategyReportThemedCardValuesLabels.MAX_STOCK_DROP,
        ),
      ),
    [values],
  );

  const oea = useMemo(
    () =>
      drawdownRowFigure(
        values.find(
          (v) => v.label === StrategyReportThemedCardValuesLabels.OEA_DROP,
        ),
      ),
    [values],
  );

  // The bar GEOMETRY below needs a number whatever the row holds, and a row
  // with no figure draws no bar at all -- so the 0 here is a width, never a
  // drawdown, and it cannot reach a label: every label reads `state` first.
  const stockDrop = stock.figure ?? 0;
  const oeaDrop = oea.figure ?? 0;

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const negativeLimitMagnitude = Math.abs(negativeLimit);

  // Only ever asked of two real figures: this splits the track so a positive
  // and a negative bar can be told apart, and a row with no figure has no
  // sign to be opposite.
  //
  // The two guards are an INVARIANT, not a bug fix, and the difference is
  // worth stating: an absent figure already resolves to a geometry 0 above,
  // and 0 satisfies neither `< 0` nor `> 0`, so both disjuncts were false for
  // it either way. What the guards buy is that the expression no longer
  // depends on that coincidence -- change the geometry fallback off 0 and this
  // still asks only about figures.
  const hasMixedSigns =
    stock.figure !== null &&
    oea.figure !== null &&
    ((stockDrop < 0 && oeaDrop > 0) || (stockDrop > 0 && oeaDrop < 0));

  const stockPercentage = getBarWidthPercentage(
    stockDrop,
    maxDrop,
    negativeLimitMagnitude,
  );
  const oeaPercentage = getBarWidthPercentage(
    oeaDrop,
    maxDrop,
    negativeLimitMagnitude,
  );

  const stockFilledWidth = getBarWidth(stockPercentage, hasMixedSigns);
  const oeaFilledWidth = getBarWidth(oeaPercentage, hasMixedSigns);

  const stockBarDirection = getBarDirection(stockDrop);
  const oeaBarDirection = getBarDirection(oeaDrop);

  const rows = [
    {
      key: "stock",
      label: "Stock",
      value: stockDrop,
      state: stock.state,
      width: stockFilledWidth,
      direction: stockBarDirection,
      filledStyle: drawdownComparisonChartStyles.barStockFilled,
      valueStyle: drawdownComparisonChartStyles.barStockValue,
      rowSx: drawdownComparisonChartStyles.barRow,
    },
    {
      key: "oea",
      label: "OEA",
      value: oeaDrop,
      state: oea.state,
      width: oeaFilledWidth,
      direction: oeaBarDirection,
      filledStyle: drawdownComparisonChartStyles.barOeaFilled,
      valueStyle: drawdownComparisonChartStyles.barOeaValue,
      rowSx: {
        ...drawdownComparisonChartStyles.barRow,
        marginBottom: 0,
      },
    },
  ] as const;

  return (
    <>
      <Card
        sx={drawdownComparisonChartStyles.card}
        onClick={openModal}
        tabIndex={0}
        role="button"
        aria-label="Click to edit drawdown limit"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openModal();
          }
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs="auto">
            <Box sx={drawdownComparisonChartStyles.titleContainer}>
              <Typography sx={drawdownComparisonChartStyles.titleLine1}>
                Drawdown
              </Typography>
              <Typography sx={drawdownComparisonChartStyles.titleLine2}>
                Comparison
              </Typography>
            </Box>
          </Grid>
          <Grid item xs>
            <Box sx={drawdownComparisonChartStyles.barsContainer}>
              {rows.map((row) => (
                <Box key={row.key} sx={row.rowSx}>
                  <Typography sx={drawdownComparisonChartStyles.barLabel}>
                    {row.label}
                  </Typography>
                  {/* The empty TRACK is kept when there is no figure, and
                      only the filled bar goes: it holds the row on the same
                      line as its neighbour, so a card with one figure and
                      one state does not read as a chart that half failed to
                      render. */}
                  <Box sx={drawdownComparisonChartStyles.barContainer}>
                    {row.state === null && (
                      <Box
                        sx={{
                          ...row.filledStyle,
                          width: row.width,
                          left:
                            row.direction === "left"
                              ? getBarOffset(hasMixedSigns)
                              : "auto",
                          right:
                            row.direction === "right"
                              ? getBarOffset(hasMixedSigns)
                              : "auto",
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    sx={
                      row.state === null
                        ? row.valueStyle
                        : drawdownComparisonChartStyles.barStateValue
                    }
                  >
                    {row.state ?? asBarLabel(row.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Card>

      <DrawdownComparisonEditModal
        isOpened={isModalOpen}
        onClose={closeModal}
        maxDrop={maxDrop}
        negativeLimit={negativeLimit}
        onLimitsChange={onLimitsChange}
      />
    </>
  );
};
